import React from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../src/context/AuthContext';
import { useTranslation } from '../../src/hooks/useTranslation';
import { pathForPhase } from '../../src/navigation/phaseRedirect';
import { ROUTES } from '../../src/navigation/routes';
import { goBackOr } from '../../src/navigation/inAppHistory';
import { chatAPI, taskAPI } from '../../src/services/api';
import type { ChatMessage } from '../../src/types';
import { trackFeatureUsage } from '../../src/services/analytics';
import { brand } from '../../src/design-system/theme/tokens';

type ScenarioId = 'overwhelmed_leader' | 'career_transition' | 'confidence_after_feedback';

interface PracticeScenario {
  id: ScenarioId;
  title: string;
  description: string;
  context: string;
}

const COLORS = {
  primary: brand.primary,
  secondary: '#D97706',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
  },
};

function buildPracticeSystemPrompt(language: string, scenario: PracticeScenario) {
  if (language === 'tr') {
    return `Sen simüle edilmiş bir koçluk pratiğinde müşteri rolünü oynuyorsun.

KURALAR:
- Asla koç rolüne geçme.
- Asla analist, öğretmen veya değerlendiren kişi gibi konuşma.
- Gerçek bir müşteri gibi konuş: kısa ama anlamlı, dürüst, duygusu olan ve bazen kararsız.
- Her yanıtta en fazla 2-4 cümle kullan.
- Koçun sorularına doğrudan yanıt ver ama tüm hikayeyi tek seferde dökme; bilgi kademeli açılsın.
- Koçluk oturumu boyunca karakterde kal.
- Türkçe konuş.

SENARYO:
${scenario.context}

Oturum başlarken kendini bu senaryodaki müşteri olarak tanıt ve koçluk almak istediğin durumu anlat.`;
  }

  return `You are playing the client in a simulated coaching practice session.

RULES:
- Never switch into the coach role.
- Never act like an analyst, teacher, or evaluator.
- Speak like a real client: concise, emotionally grounded, and sometimes uncertain.
- Keep each reply to 2-4 sentences.
- Answer the coach directly, but reveal context gradually instead of dumping the full story at once.
- Stay in character for the full session.
- Respond in English.

SCENARIO:
${scenario.context}

When the session begins, introduce yourself as the client in this scenario and explain why you wanted coaching today.`;
}

export default function PracticeSessionRoute() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t, language } = useTranslation();
  const { phase, refreshAuth } = useAuth();
  const [requestedRefresh, setRequestedRefresh] = React.useState(false);
  const [sawLoadingAfterRefresh, setSawLoadingAfterRefresh] = React.useState(false);

  const [selectedScenarioId, setSelectedScenarioId] = React.useState<ScenarioId>('overwhelmed_leader');
  const [sessionId, setSessionId] = React.useState<number | null>(null);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [draft, setDraft] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const messagesScrollRef = React.useRef<ScrollView | null>(null);

  React.useEffect(() => {
    if (phase === 'authenticated') {
      setRequestedRefresh(false);
      setSawLoadingAfterRefresh(false);
      return;
    }

    if (requestedRefresh && phase === 'loading') {
      setSawLoadingAfterRefresh(true);
      return;
    }

    if (!requestedRefresh && phase !== 'loading') {
      setRequestedRefresh(true);
      void refreshAuth();
    }
  }, [phase, refreshAuth, requestedRefresh]);

  React.useEffect(() => {
    if (!requestedRefresh || !sawLoadingAfterRefresh || phase === 'loading' || phase === 'authenticated') {
      return;
    }

    router.replace(pathForPhase(phase) as any);
  }, [phase, requestedRefresh, router, sawLoadingAfterRefresh]);

  React.useEffect(() => {
    const handle = requestAnimationFrame(() => {
      messagesScrollRef.current?.scrollToEnd({ animated: true });
    });
    return () => cancelAnimationFrame(handle);
  }, [messages]);

  const scenarios: PracticeScenario[] = React.useMemo(
    () => [
      {
        id: 'overwhelmed_leader',
        title: t('dashboard.practiceSession.scenarios.overwhelmedLeader.title'),
        description: t(
          'dashboard.practiceSession.scenarios.overwhelmedLeader.description'
        ),
        context: t(
          'dashboard.practiceSession.scenarios.overwhelmedLeader.context'
        ),
      },
      {
        id: 'career_transition',
        title: t('dashboard.practiceSession.scenarios.careerTransition.title'),
        description: t(
          'dashboard.practiceSession.scenarios.careerTransition.description'
        ),
        context: t(
          'dashboard.practiceSession.scenarios.careerTransition.context'
        ),
      },
      {
        id: 'confidence_after_feedback',
        title: t('dashboard.practiceSession.scenarios.confidenceAfterFeedback.title'),
        description: t(
          'dashboard.practiceSession.scenarios.confidenceAfterFeedback.description'
        ),
        context: t(
          'dashboard.practiceSession.scenarios.confidenceAfterFeedback.context'
        ),
      },
    ],
    [t]
  );

  const selectedScenario = scenarios.find((item) => item.id === selectedScenarioId) || scenarios[0];

  const refreshMessages = React.useCallback(async (targetSessionId: number) => {
    const nextMessages = await chatAPI.getMessages(targetSessionId);
    setMessages(nextMessages);
  }, []);

  const beginPractice = async () => {
    if (starting || phase !== 'authenticated') {
      return;
    }

    setStarting(true);
    setError(null);

    try {
      trackFeatureUsage('practice_session', 'started', { scenario: selectedScenario.id });
      const asyncResponse = await chatAPI.sendLegacyMessage({
        message: t(
          'dashboard.practiceSession.startPrompt'
        ),
        language,
        system_prompt: buildPracticeSystemPrompt(language, selectedScenario),
        session_type: 'practice',
        session_title: `${t('dashboard.practiceSession.sessionPrefix')}: ${selectedScenario.title}`,
      });

      await taskAPI.pollForCompletion(asyncResponse.task_id).catch(() => undefined);
      setSessionId(asyncResponse.session_id);
      await refreshMessages(asyncResponse.session_id);
    } catch (error) {
      console.error('Failed to start practice session:', error);
      setError(
        t(
          'dashboard.practiceSession.startFailed'
        )
      );
    } finally {
      setStarting(false);
    }
  };

  const sendReply = async () => {
    if (!draft.trim() || !sessionId || sending) {
      return;
    }

    const nextDraft = draft.trim();
    setDraft('');
    setSending(true);
    setError(null);

    try {
      trackFeatureUsage('practice_session', 'message_sent', {
        scenario: selectedScenario.id,
        session_id: sessionId,
      });
      await chatAPI.sendLegacyMessageWithPolling({
        message: nextDraft,
        session_id: sessionId,
        language,
        system_prompt: buildPracticeSystemPrompt(language, selectedScenario),
        session_type: 'practice',
      });
      await refreshMessages(sessionId);
    } catch (error) {
      console.error('Failed to send practice-session reply:', error);
      setDraft(nextDraft);
      setError(
        t(
          'dashboard.practiceSession.replyFailed'
        )
      );
    } finally {
      setSending(false);
    }
  };

  if (phase !== 'authenticated') {
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <View className="flex-1 items-center justify-center p-8">
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('dashboard.loadingData')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View
          style={[
            styles.container,
            {
              paddingTop: 20,
              paddingBottom: Math.max(insets.bottom + 16, 24),
            },
          ]}
        >
          <View style={styles.headerRow}>
            <TouchableOpacity onPress={() => goBackOr(router, ROUTES.TABS_DASHBOARD)} style={styles.iconButton}>
              <MaterialIcons name="arrow-back" size={22} color={COLORS.text.primary} />
            </TouchableOpacity>
            <View style={styles.headerCopy}>
              <Text style={styles.eyebrow}>
                {t('dashboard.practiceSession.eyebrow')}
              </Text>
              <Text style={styles.title}>
                {t('dashboard.practiceSession.title')}
              </Text>
            </View>
          </View>

          <ScrollView
            ref={messagesScrollRef}
            style={styles.flex}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <Card>
              <CardContent className="p-5">
                <Text style={styles.cardTitle}>
                  {t('dashboard.practiceSession.subtitle')}
                </Text>
                <Text style={styles.cardBody}>
                  {t(
                    'dashboard.practiceSession.description'
                  )}
                </Text>
              </CardContent>
            </Card>

            <View style={styles.scenarioList}>
              {scenarios.map((scenario) => {
                const selected = scenario.id === selectedScenario.id;
                return (
                  <TouchableOpacity
                    key={scenario.id}
                    style={[styles.scenarioCard, selected && styles.scenarioCardSelected]}
                    activeOpacity={0.8}
                    onPress={() => {
                      if (!sessionId) {
                        setSelectedScenarioId(scenario.id);
                      }
                    }}
                    disabled={Boolean(sessionId)}
                  >
                    <View style={styles.scenarioHeader}>
                      <View style={[styles.scenarioIcon, selected && styles.scenarioIconSelected]}>
                        <MaterialIcons
                          name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                          size={18}
                          color={selected ? '#FFF' : COLORS.primary}
                        />
                      </View>
                      <View style={styles.scenarioText}>
                        <Text style={styles.scenarioTitle}>{scenario.title}</Text>
                        <Text style={styles.scenarioDescription}>{scenario.description}</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            {!sessionId ? (
              <Button
                variant="default"
                size="lg"
                onPress={beginPractice}
                disabled={starting}
              >
                <Text style={styles.buttonText}>
                  {starting
                    ? t('dashboard.practiceSession.starting')
                    : t('dashboard.practiceSession.startCta')}
                </Text>
              </Button>
            ) : (
              <>
                <Card>
                  <CardContent className="p-5">
                    <View style={styles.sessionHeader}>
                      <View>
                        <Text style={styles.cardTitle}>
                          {selectedScenario.title}
                        </Text>
                        <Text style={styles.cardBody}>
                          {t(
                            'dashboard.practiceSession.sessionHint'
                          )}
                        </Text>
                      </View>
                      <TouchableOpacity
                        style={styles.iconButton}
                        onPress={() =>
                          router.push({
                            pathname: ROUTES.TABS_CHAT,
                            params: { sessionId: String(sessionId) },
                          })
                        }
                      >
                        <MaterialIcons name="open-in-new" size={20} color={COLORS.primary} />
                      </TouchableOpacity>
                    </View>

                    <View style={styles.messagesList}>
                      {messages.map((message) => (
                        <View
                          key={`${message.id}-${message.created_at}`}
                          style={[
                            styles.messageRow,
                            message.role === 'user' ? styles.userRow : styles.assistantRow,
                          ]}
                        >
                          <View
                            style={[
                              styles.messageBubble,
                              message.role === 'user' ? styles.userBubble : styles.assistantBubble,
                            ]}
                          >
                            <Text
                              style={message.role === 'user' ? styles.userMessage : styles.assistantMessage}
                            >
                              {message.content}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <TextInput
                      style={styles.input}
                      value={draft}
                      onChangeText={setDraft}
                      multiline
                      placeholder={t(
                        'dashboard.practiceSession.inputPlaceholder'
                      )}
                      placeholderTextColor="#9CA3AF"
                    />
                    <View style={styles.actionRow}>
                      <Button
                        variant="default"
                        size="lg"
                        onPress={sendReply}
                        disabled={!draft.trim() || sending}
                      >
                        <Text style={styles.buttonText}>
                          {sending
                            ? t('dashboard.practiceSession.sending')
                            : t('common.send')}
                        </Text>
                      </Button>
                    </View>
                  </CardContent>
                </Card>
              </>
            )}

            {error ? (
              <Card>
                <CardContent className="p-5">
                  <Text style={styles.errorText}>{error}</Text>
                </CardContent>
              </Card>
            ) : null}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  headerCopy: {
    flex: 1,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: COLORS.primary,
    marginBottom: 4,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: COLORS.text.primary,
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(30, 64, 175, 0.08)',
  },
  scrollContent: {
    gap: 16,
    paddingBottom: 16,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: 8,
  },
  cardBody: {
    fontSize: 14,
    lineHeight: 21,
    color: COLORS.text.secondary,
  },
  scenarioList: {
    gap: 12,
  },
  scenarioCard: {
    borderRadius: 20,
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(30, 64, 175, 0.08)',
  },
  scenarioCardSelected: {
    borderColor: 'rgba(30, 64, 175, 0.32)',
    backgroundColor: 'rgba(30, 64, 175, 0.06)',
  },
  scenarioHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scenarioIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(30, 64, 175, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scenarioIconSelected: {
    backgroundColor: COLORS.primary,
  },
  scenarioText: {
    flex: 1,
  },
  scenarioTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: 4,
  },
  scenarioDescription: {
    fontSize: 14,
    lineHeight: 20,
    color: COLORS.text.secondary,
  },
  buttonText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '700',
  },
  sessionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  messagesList: {
    gap: 10,
  },
  messageRow: {
    flexDirection: 'row',
  },
  assistantRow: {
    justifyContent: 'flex-start',
  },
  userRow: {
    justifyContent: 'flex-end',
  },
  messageBubble: {
    maxWidth: '84%',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 18,
  },
  assistantBubble: {
    backgroundColor: '#F3F4F6',
    borderTopLeftRadius: 8,
  },
  userBubble: {
    backgroundColor: COLORS.primary,
    borderTopRightRadius: 8,
  },
  assistantMessage: {
    color: COLORS.text.primary,
    fontSize: 14,
    lineHeight: 20,
  },
  userMessage: {
    color: '#FFF',
    fontSize: 14,
    lineHeight: 20,
  },
  input: {
    minHeight: 92,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFF',
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: COLORS.text.primary,
    fontSize: 15,
    textAlignVertical: 'top',
  },
  actionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 14,
  },
  errorText: {
    color: '#B91C1C',
    fontSize: 14,
    lineHeight: 20,
  },
  loadingText: {
    marginTop: 14,
    color: COLORS.text.secondary,
    fontSize: 15,
  },
});
